import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Toolbar,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import FooterSection from '../components/FooterSection';

const productGuides = {
  darwin: {
    label: 'Darwin',
    tagline: 'Pipe-mounted temperature & flow sensor',
    image: '/drawing_for_manual_darwin.jpeg',
    imageAlt: 'Darwin sensor diagram showing key components and buttons',
    video: '/videos/darwin_installation.mp4',
    steps: [
      {
        number: '01',
        title: 'Mount the sensor',
        description:
          'Position the Darwin sensor at your chosen monitoring point, secure the probe firmly against the pipe surface using the supplied fixings, and connect the probe connector to the sensor probe socket. Ensure good contact between the probe and pipe for accurate temperature readings.',
      },
      {
        number: '02',
        title: 'Activate and pair',
        description:
          'Remove the battery pull-tab and press the Device button - the LED will turn solid green. Then press the Connect button - the LED will turn solid blue. Connect via the app - the LED will turn solid white. Configure your device settings, and tap Provision to complete setup. The LED will cycle through multiple colours and turn off. Your sensor is now live and will start reporting data to your dashboard.',
      },
      {
        number: '03',
        title: 'Add to your dashboard',
        description:
          'In the app, assign the sensor to a site and location. Give each probe a meaningful name and set any temperature offsets. Tap Install and the device will appear on your dashboard ready to use.',
      },
      {
        number: '04',
        title: 'Configure alerts',
        description:
          'Set up your configurable rules by choosing from temperature, temperature delta, flow, and no-flow. You\'ll receive an email notification whenever a rule is triggered.',
      },
      {
        number: '05',
        title: 'Monitor and report',
        description:
          'All readings are logged automatically. Export compliance reports at any time from the Reports section of your dashboard.',
      },
    ],
  },
  eden: {
    label: 'Eden',
    tagline: 'Environmental temperature & humidity sensor',
    image: '/drawing_for_manual_eden.jpeg',
    imageAlt: 'Eden sensor diagram showing key components and buttons',
    steps: [
      {
        number: '01',
        title: 'Place the sensor',
        description:
          'Position the Eden sensor inside the unit or environment you wish to monitor. Avoid placing the sensor directly against cooling elements or in airflow streams to ensure representative readings.',
      },
      {
        number: '02',
        title: 'Activate and pair',
        description:
          'Remove the battery pull-tab and press the Device button - the LED will turn solid green. Then press the Connect button - the LED will turn solid blue. Connect via the app - the LED will turn solid white. Configure your device settings, and tap Provision to complete setup. The LED will cycle through multiple colours and turn off. Your sensor is now live and will start reporting data to your dashboard.',
      },
      {
        number: '03',
        title: 'Add to your dashboard',
        description:
          'In the app, assign the sensor to a site and location. No additional configuration is needed. Tap Install and the device will appear on your dashboard ready to use.',
      },
      {
        number: '04',
        title: 'Configure alerts',
        description:
          'Set up your configurable rules by choosing from temperature and temperature delta. You\'ll receive an email notification whenever a rule is triggered.',
      },
      {
        number: '05',
        title: 'Monitor and report',
        description:
          'All readings are logged automatically. Export compliance reports at any time from the Reports section of your dashboard.',
      },
    ],
  },
  gateway: {
    label: 'Gateway',
    tagline: 'Connect your hub directly to your network for the most reliable connection',
    image: '/drawing_for_manual_gateway_eth.jpeg',
    imageAlt: 'Gateway diagram showing ports and status indicators',
    steps: [
      {
        number: '01',
        title: 'Position the gateway',
        description:
          'Place the gateway centrally within your site, ideally elevated and clear of large metal obstructions, to maximise wireless coverage for your sensors. Ensure it is within range of a power outlet and your network connection.',
      },
      {
        number: '02',
        title: 'Connect power and network',
        description:
          'Plug the gateway into a mains power outlet using the supplied adapter. Connect it to your local network via Ethernet for the most reliable connection, or view the Gateway Wi-Fi installation guide if a wired connection is not available.',
      },
      {
        number: '03',
        title: 'Power on and verify',
        description:
          'Wait for the status LED to turn breathing blue, indicating the gateway has booted and connected to the Sigmatiqa cloud. If the LED remains green or red, check your network connection and refer to the troubleshooting section of the FAQs.',
      },
      {
        number: '04',
        title: 'Register the gateway',
        description:
          'In the app, scan the QR code on the underside of the gateway or enter the serial number manually. Assign the gateway to a site and location - any sensors provisioned at the gateway will automatically connect and route through it.',
      },
      {
        number: '05',
        title: 'Pair your sensors',
        description:
          'Provision your Darwin and Eden sensors as normal. Once provisioned to the gateway, they will connect to the gateway automatically and begin reporting data to your dashboard.',
      },
    ],
  },
  gatewaywifi: {
    label: 'Gateway Wi-Fi',
    tagline: 'Connecting your Gateway to your Wi-Fi',
    image: '/drawing_for_manual_gateway.jpeg',
    imageAlt: 'Gateway diagram showing ports and status indicators',
    steps: [
      {
        number: '01',
        title: 'Connect power and network',
        description:
          'Plug the gateway into a mains power outlet using the supplied adapter. Connect it directly to your PC via the provided Ethernet cable.',
      },
      {
        number: '02',
        title: 'Power on and access setup portal',
        description:
          'Turn on the power outlet if not already on. Wait for the gateway to boot and the status LED to turn green or blue. Open a web browser and navigate to http://xxx.xxx.xxx.xxx (the gateway\'s IP address provided with the gateway).',
      },
      {
        number: '03',
        title: 'Log in and configure Wi-Fi',
        description:
          'Log in to the gateway setup portal using the default credentials (provided with the gateway). Navigate to the Wi-Fi settings page, scan for available networks, and select your Wi-Fi network. Enter your Wi-Fi password and save the settings.',
      },
      {
        number: '04',
        title: 'Verify Wi-Fi connection',
        description:
          'Reboot the gateway and ensure it is connected to your Wi-Fi network by checking the status LED. It should turn blue, indicating a successful connection. If the LED remains green or red, recheck your Wi-Fi settings and try again.',
      }
    ]
  }
};

const InstallationPage = () => {
  const [product, setProduct] = useState('darwin');
  const guide = productGuides[product];
  if (new URLSearchParams(window.location.search).get('video') === '1') {
    sessionStorage.setItem('showInstallationVideo', '1');
  }
  const showVideo = sessionStorage.getItem('showInstallationVideo') === '1';

  return (
    <Box sx={{ backgroundColor: '#ffffff', minHeight: '100vh' }}>
      <Toolbar sx={{ height: '68px' }} />

      {/* Page hero */}
      <Box sx={{ backgroundColor: '#000000', py: { xs: 8, md: 12 }, borderBottom: '1px solid #1a1a1a' }}>
        <Container maxWidth="lg">
          <Typography
            component="h1"
            sx={{
              fontFamily: 'Outfit',
              fontWeight: 700,
              fontSize: { xs: '2.5rem', md: '3.5rem' },
              color: '#ffffff',
              letterSpacing: '-0.02em',
              mb: 2,
            }}
          >
            Installation Guide
          </Typography>
          <Typography
            sx={{
              fontFamily: 'Outfit',
              fontWeight: 300,
              fontSize: '1.15rem',
              color: 'rgba(255,255,255,0.6)',
              maxWidth: 520,
            }}
          >
            Get your sensors up and running in under 10 minutes. No tools or
            specialist knowledge required.
          </Typography>
        </Container>
      </Box>

      {/* Product selector */}
      <Box sx={{ backgroundColor: '#fafafa', borderBottom: '1px solid #eeeeee', py: { xs: 4, md: 5 } }}>
        <Container maxWidth="md">
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              alignItems: { xs: 'flex-start', sm: 'center' },
              gap: { xs: 2, sm: 4 },
            }}
          >
            <Box sx={{ flex: 1 }}>
              <Typography
                sx={{
                  fontFamily: 'Outfit',
                  fontWeight: 700,
                  fontSize: '0.8rem',
                  color: '#004D40',
                  letterSpacing: '0.08em',
                  textTransform: 'uppercase',
                  mb: 0.5,
                }}
              >
                Choose your product
              </Typography>
              <Typography
                sx={{
                  fontFamily: 'Outfit',
                  fontWeight: 400,
                  fontSize: '0.95rem',
                  color: '#555555',
                }}
              >
                {guide.tagline}
              </Typography>
            </Box>
            <FormControl sx={{ minWidth: { xs: '100%', sm: 240 } }} size="medium">
              <InputLabel id="product-select-label" sx={{ fontFamily: 'Outfit' }}>
                Product
              </InputLabel>
              <Select
                labelId="product-select-label"
                id="product-select"
                value={product}
                label="Product"
                onChange={(e) => setProduct(e.target.value)}
                sx={{
                  fontFamily: 'Outfit',
                  backgroundColor: '#ffffff',
                }}
              >
                {Object.entries(productGuides).map(([key, g]) => (
                  <MenuItem key={key} value={key} sx={{ fontFamily: 'Outfit' }}>
                    {g.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </Container>
      </Box>

      {/* Steps */}
      <Box sx={{ py: { xs: 8, md: 12 } }}>
        <Container maxWidth="md">
          {/* Device diagram */}
          {guide.image && (
            <Box
              sx={{
                mb: { xs: 6, md: 8 },
                display: 'flex',
                justifyContent: 'center',
              }}
            >
              <Box
                component="img"
                src={guide.image}
                alt={guide.imageAlt}
                sx={{
                  width: '100%',
                  maxWidth: 720,
                  height: 'auto',
                  borderRadius: 2,
                  border: '1px solid #e8e8e8',
                  display: 'block',
                }}
              />
            </Box>
          )}

          {/* Installation video */}
          {guide.video && showVideo && (
            <Box
              sx={{
                mb: { xs: 6, md: 8 },
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 2,
              }}
            >
              <Typography
                sx={{
                  fontFamily: 'Outfit',
                  fontWeight: 700,
                  fontSize: '1.1rem',
                  color: '#000000',
                  alignSelf: 'flex-start',
                }}
              >
                Installation Video
              </Typography>
              <Box
                component="video"
                src={guide.video}
                controls
                sx={{
                  width: '100%',
                  maxWidth: 720,
                  height: 'auto',
                  borderRadius: 2,
                  border: '1px solid #e8e8e8',
                  display: 'block',
                  backgroundColor: '#000000',
                }}
              />
            </Box>
          )}

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
            {guide.steps.map((step, i) => (
              <Box
                key={step.number}
                sx={{
                  display: 'flex',
                  gap: { xs: 3, md: 5 },
                  pb: i < guide.steps.length - 1 ? 6 : 0,
                  position: 'relative',
                }}
              >
                {/* Step number + connecting line */}
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    flexShrink: 0,
                  }}
                >
                  <Box
                    sx={{
                      width: 48,
                      height: 48,
                      borderRadius: '50%',
                      backgroundColor: '#004D40',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                    }}
                  >
                    <Typography
                      sx={{
                        fontFamily: 'Outfit',
                        fontWeight: 700,
                        fontSize: '0.8rem',
                        color: '#ffffff',
                        letterSpacing: '0.05em',
                      }}
                    >
                      {step.number}
                    </Typography>
                  </Box>
                  {i < guide.steps.length - 1 && (
                    <Box sx={{ width: '2px', flex: 1, backgroundColor: '#e8e8e8', mt: 1 }} />
                  )}
                </Box>

                {/* Content */}
                <Box sx={{ pt: 0.75 }}>
                  <Typography
                    sx={{
                      fontFamily: 'Outfit',
                      fontWeight: 700,
                      fontSize: '1.2rem',
                      color: '#000000',
                      mb: 1,
                    }}
                  >
                    {step.title}
                  </Typography>
                  <Typography
                    sx={{
                      fontFamily: 'Outfit',
                      fontWeight: 400,
                      fontSize: '0.97rem',
                      color: '#555555',
                      lineHeight: 1.75,
                    }}
                  >
                    {step.description}
                  </Typography>
                </Box>
              </Box>
            ))}
          </Box>
        </Container>
      </Box>

      <FooterSection />
    </Box>
  );
};

export default InstallationPage;
